import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { IFormPlainDataListboxPropsChangeState, IFormSelectInputOption } from '@modules/forms/types';
import type {
  ChangeGroupOwnerPayload,
  IGroupMembersResponse,
} from '@modules/groups-of-admins/submodules/edit/types';
import { useGroupOwnersListQuery } from '@modules/groups-of-admins/submodules/create/queries';
import { GroupMemberAction } from '@modules/groups-of-admins/submodules/edit/enums';
import {
  useGroupsOfAdminsChangeGroupOwnerMutation,
  useGroupsOfAdminsChangeMemberMutation,
} from '@modules/groups-of-admins/submodules/edit/mutations';
import {
  useAvailableAdminsListQuery,
  useCurrentAdminOwnerDataQuery,
} from '@modules/groups-of-admins/submodules/edit/queries';

interface GroupsOfAdminEditPageComposable {
  currentAdminValue: Ref<string>;
  selectValue: Ref<number | undefined>;
  groupMembersList: Ref<IGroupMembersResponse | undefined>;
  isLoadingGroupMembers: Ref<boolean>;
  isLoadingCurrentAdminData: ComputedRef<boolean>;
  isLoadingAdminOwnerChange: Ref<boolean>;
  ownersDataOptions: ComputedRef<IFormSelectInputOption<number>[]>;
  onChange(value?: number): Promise<void>;
  onChangeParticipant(state: IFormPlainDataListboxPropsChangeState<number>): void;
}

export default function useGroupsOfAdminEditPage(): GroupsOfAdminEditPageComposable {
  const selectValue = ref<number>();
  const route = useRoute();
  const id = computed<number | undefined>(() => (
    route.params.id ? Number(route.params.id) : undefined
  ));

  const {
    data: groupOwnersList,
  } = useGroupOwnersListQuery();

  const { data: currentAdmin, isLoading: isLoadingCurrentAdmin } = useCurrentAdminOwnerDataQuery(id);

  const { data: groupMembersList, isLoading: isLoadingGroupsMemberList } = useAvailableAdminsListQuery(id);

  const { mutateAsync: changeGroupOwner, isLoading: isLoadingAdminOwnerChange } = useGroupsOfAdminsChangeGroupOwnerMutation();

  const { mutate: onChangeMember, isLoading: isLoadingGroupMembers } = useGroupsOfAdminsChangeMemberMutation();

  const currentAdminValue = computed<string>(() => `${currentAdmin.value?.root_admin.id} - ${currentAdmin.value?.root_admin.name}`);

  const isLoadingCurrentAdminData = computed(() => (
    isLoadingGroupsMemberList.value || (!!id.value && isLoadingCurrentAdmin.value)
  ));

  const ownersDataOptions = computed<IFormSelectInputOption<number>[]>(() => {
    if (!groupOwnersList.value) {
      return [];
    }

    return groupOwnersList.value.map((item) => ({
      value: item.id,
      label: `${item.name}`,
    }));
  });

  async function onChange(value?: number): Promise<void> {
    if (value) {
      selectValue.value = value;
      const payload: ChangeGroupOwnerPayload = { admin_id: value, id: id.value };
      await changeGroupOwner(payload);
      selectValue.value = undefined;
    }
  }

  function onChangeParticipant(state: IFormPlainDataListboxPropsChangeState<number>): void {
    const { values, check, ...payload } = state;
    const action = check ? GroupMemberAction.ADD : GroupMemberAction.REMOVE;

    onChangeMember({
      id: id.value,
      action,
      admin: payload,
    });
  }

  return {
    selectValue,
    currentAdminValue,
    groupMembersList,
    isLoadingGroupMembers,
    isLoadingCurrentAdminData,
    isLoadingAdminOwnerChange,
    ownersDataOptions,
    onChange,
    onChangeParticipant,
  };
}
